/**
 * ProjectionsChart Component
 * Interactive line chart showing box office projection trends over time
 */

import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Info,
  Download,
  GetApp
} from '@mui/icons-material';
import { formatCurrency, formatProjectionDate } from '../../types/boxOfficeTypes';

const ProjectionsChart = ({ 
  projections, 
  filmTitle, 
  loading = false, 
  error = null 
}) => {
  const [chartType, setChartType] = React.useState('all');

  // Handle export chart as PNG
  const handleExportChart = () => {
    try {
      // Get the chart container
      const chartContainer = document.querySelector('.recharts-wrapper');
      if (!chartContainer) {
        console.error('Chart container not found');
        return;
      }

      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const svg = chartContainer.querySelector('svg');
      
      if (!svg) {
        console.error('SVG element not found');
        return;
      }

      // Set canvas dimensions
      canvas.width = svg.clientWidth;
      canvas.height = svg.clientHeight;

      // Convert SVG to canvas (simplified approach)
      const svgData = new XMLSerializer().serializeToString(svg);
      const img = new Image();
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);

        // Download the image
        const link = document.createElement('a');
        link.download = `box-office-projections-${filmTitle?.replace(/[^a-zA-Z0-9]/g, '-') || 'chart'}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      };

      img.src = url;
    } catch (error) {
      console.error('Failed to export chart:', error);
    }
  };

  // Handle export data as CSV
  const handleExportData = () => {
    try {
      if (!chartData || chartData.length === 0) {
        console.error('No data to export');
        return;
      }

      // Create CSV content
      const headers = [
        'Date',
        'Formatted Date',
        'Actual Projection',
        'Projection Floor',
        'Projection Ceiling',
        'Outlier Minimum',
        'Outlier Ceiling',
        'Days Until Release',
        'Weeks Until Release',
        'Created By',
        'Notes'
      ];

      const csvContent = [
        headers.join(','),
        ...chartData.map(row => [
          row.date,
          row.formattedDate,
          row.actualProjection,
          row.projectionFloor,
          row.projectionCeiling,
          row.outlierMinimum,
          row.outlierCeiling,
          row.daysUntilRelease,
          row.weeksUntilRelease,
          `"${row.createdBy || ''}"`,
          `"${row.notes || ''}"`
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `box-office-projections-${filmTitle?.replace(/[^a-zA-Z0-9]/g, '-') || 'data'}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to export data:', error);
    }
  };

  // Transform projections data for chart
  const chartData = useMemo(() => {
    if (!projections || projections.length === 0) return [];

    // Helper function to extract date value from API date objects
    const getDateValue = (dateInput) => {
      if (typeof dateInput === 'object' && dateInput.value) {
        return dateInput.value;
      }
      return dateInput;
    };

    // Sort projections by date (ascending - oldest on left, newest on right)
    const sortedProjections = [...projections].sort((a, b) => {
      const dateA = getDateValue(a.projectionDate);
      const dateB = getDateValue(b.projectionDate);
      return new Date(dateA) - new Date(dateB);
    });

    return sortedProjections.map(projection => ({
      date: getDateValue(projection.projectionDate),
      formattedDate: formatProjectionDate(projection.projectionDate),
      actualProjection: projection.actualProjection,
      projectionFloor: projection.projectionFloor,
      projectionCeiling: projection.projectionCeiling,
      outlierMinimum: projection.outlierMinimum,
      outlierCeiling: projection.outlierCeiling,
      daysUntilRelease: projection.daysUntilRelease,
      weeksUntilRelease: projection.weeksUntilRelease,
      createdBy: projection.createdBy,
      notes: projection.notes
    }));
  }, [projections]);

  // Get latest projection for reference
  const latestProjection = useMemo(() => {
    if (!projections || projections.length === 0) return null;
    
    // Helper function to extract date value from API date objects
    const getDateValue = (dateInput) => {
      if (typeof dateInput === 'object' && dateInput.value) {
        return dateInput.value;
      }
      return dateInput;
    };
    
    return projections.reduce((latest, current) => {
      const currentDate = getDateValue(current.projectionDate);
      const latestDate = getDateValue(latest.projectionDate);
      return new Date(currentDate) > new Date(latestDate) ? current : latest;
    });
  }, [projections]);

  // Calculate trend
  const trend = useMemo(() => {
    if (chartData.length < 2) return 'stable';
    
    const latest = chartData[chartData.length - 1];
    const previous = chartData[chartData.length - 2];
    
    const change = latest.actualProjection - previous.actualProjection;
    const changePercentage = (change / previous.actualProjection) * 100;
    
    if (changePercentage > 5) return 'up';
    if (changePercentage < -5) return 'down';
    return 'stable';
  }, [chartData]);

  // Get trend icon and color
  const getTrendIcon = () => {
    switch (trend) {
      case 'up': return <TrendingUp sx={{ color: 'success.main' }} />;
      case 'down': return <TrendingDown sx={{ color: 'error.main' }} />;
      default: return <TrendingFlat sx={{ color: 'text.secondary' }} />;
    }
  };

  const getTrendColor = () => {
    switch (trend) {
      case 'up': return 'success.main';
      case 'down': return 'error.main';
      default: return 'text.secondary';
    }
  };

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {data.formattedDate}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2">
              <strong>Actual Projection:</strong> {formatCurrency(data.actualProjection)}
            </Typography>
            <Typography variant="body2">
              <strong>Projection Range:</strong> {formatCurrency(data.projectionFloor)} - {formatCurrency(data.projectionCeiling)}
            </Typography>
            <Typography variant="body2">
              <strong>Outlier Range:</strong> {formatCurrency(data.outlierMinimum)} - {formatCurrency(data.outlierCeiling)}
            </Typography>
            <Typography variant="body2">
              <strong>Days Until Release:</strong> {data.daysUntilRelease}
            </Typography>
            <Typography variant="body2">
              <strong>Created By:</strong> {data.createdBy}
            </Typography>
          </Box>
        </Paper>
      );
    }
    return null;
  };

  // Filter chart data based on selected type
  const getFilteredData = () => {
    if (chartType === 'all') return chartData;
    if (chartType === 'core') {
      return chartData.map(item => ({
        ...item,
        outlierMinimum: null,
        outlierCeiling: null
      }));
    }
    return chartData;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (!projections || projections.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No Projection Data Available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create projections to see trend analysis
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Chart Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Projection Trends
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Trend:
            </Typography>
            {getTrendIcon()}
            <Chip
              label={trend.toUpperCase()}
              size="small"
              sx={{ 
                backgroundColor: `${getTrendColor()}.light`,
                color: `${getTrendColor()}.main`,
                fontWeight: 600
              }}
            />
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Chart Type</InputLabel>
            <Select
              value={chartType}
              label="Chart Type"
              onChange={(e) => setChartType(e.target.value)}
            >
              <MenuItem value="all">All Metrics</MenuItem>
              <MenuItem value="core">Core Projections</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            size="small"
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportChart}
            disabled={!chartData || chartData.length === 0}
          >
            Export Chart
          </Button>
          
          <Button
            size="small"
            variant="outlined"
            startIcon={<GetApp />}
            onClick={handleExportData}
            disabled={!chartData || chartData.length === 0}
          >
            Export Data
          </Button>
        </Box>
      </Box>

      {/* Chart */}
      <Box sx={{ height: 400, mb: 2 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={getFilteredData()}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="formattedDate"
              stroke="#666"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              stroke="#666"
              fontSize={12}
              tickFormatter={(value) => `$${(value / 1000000).toFixed(0)}M`}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
            />
            
            {/* Reference line for latest projection */}
            {latestProjection && (
              <ReferenceLine 
                y={latestProjection.actualProjection} 
                stroke="#1976d2" 
                strokeDasharray="5 5"
                label={{ value: "Latest", position: "topRight" }}
              />
            )}
            
            {/* Projection range lines */}
            <Line
              type="monotone"
              dataKey="projectionFloor"
              stroke="#4caf50"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: '#4caf50', strokeWidth: 2, r: 3 }}
              name="Projection Floor"
            />
            <Line
              type="monotone"
              dataKey="projectionCeiling"
              stroke="#4caf50"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: '#4caf50', strokeWidth: 2, r: 3 }}
              name="Projection Ceiling"
            />
            
            {/* Actual projection line */}
            <Line
              type="monotone"
              dataKey="actualProjection"
              stroke="#1976d2"
              strokeWidth={3}
              dot={{ fill: '#1976d2', strokeWidth: 2, r: 5 }}
              name="Actual Projection"
            />
            
            {chartType === 'all' && (
              <>
                <Line
                  type="monotone"
                  dataKey="outlierMinimum"
                  stroke="#ff9800"
                  strokeWidth={2}
                  strokeDasharray="8 4"
                  dot={{ fill: '#ff9800', strokeWidth: 2, r: 3 }}
                  name="Outlier Range"
                />
                <Line
                  type="monotone"
                  dataKey="outlierCeiling"
                  stroke="#ff9800"
                  strokeWidth={2}
                  strokeDasharray="8 4"
                  dot={{ fill: '#ff9800', strokeWidth: 2, r: 3 }}
                  name="Outlier Ceiling"
                />
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* Chart Info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Info sx={{ fontSize: '1rem', color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">
          Projection Floor = Actual × 0.9, Projection Ceiling = Actual × 1.1
        </Typography>
      </Box>

      {/* Latest Projection Summary */}
      {latestProjection && (
        <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Latest Projection Summary
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Actual Projection
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {formatCurrency(latestProjection.actualProjection)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Projection Range
              </Typography>
              <Typography variant="body2">
                {formatCurrency(latestProjection.projectionFloor)} - {formatCurrency(latestProjection.projectionCeiling)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Days Until Release
              </Typography>
              <Typography variant="body2">
                {latestProjection.daysUntilRelease} days
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Created By
              </Typography>
              <Typography variant="body2">
                {latestProjection.createdBy}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ProjectionsChart;
